import axios from 'axios'
import { toast } from './snackbarHelper'
import { toastMessage } from '@/constants/strings'

/**
 * Handle export-related errors with user-friendly messages
 *
 * @param error - Error from export API call or file generation
 * @param context - Additional context for logging (e.g., 'CSV export', 'XLSX export')
 */
export const handleExportError = (error: unknown, context?: string): void => {
  const prefix = context ? `[${context}]` : '[Export]'
  console.error(`${prefix} Export failed:`, error)

  // Handle Axios errors (API call failures)
  if (axios.isAxiosError(error)) {
    const status = error.response?.status

    switch (status) {
      case 401:
        toast('Authentication failed. Please log in again.', { variant: 'error' })
        break
      case 403:
        toast('You do not have permission to export issues.', { variant: 'error' })
        break
      case 404:
        toast('Export endpoint not found. Please contact support.', { variant: 'error' })
        break
      case 429:
        toast('Too many export requests. Please wait a moment and try again.', { variant: 'error' })
        break
      case 500:
      case 502:
      case 503:
        toast('Server error. Please try again later.', { variant: 'error' })
        break
      default:
        toast(toastMessage.sentry.exportError, { variant: 'error' })
    }

    // Log additional error details for debugging
    if (error.response?.data) {
      console.error(`${prefix} API error response:`, error.response.data)
    }
  } else if (error instanceof Error) {
    // Handle JavaScript errors (e.g., file generation failures)
    console.error(`${prefix} Error message:`, error.message)
    console.error(`${prefix} Error stack:`, error.stack)
    toast(`Export failed: ${error.message}`, { variant: 'error' })
  } else {
    // Handle unknown error types
    console.error(`${prefix} Unknown error:`, error)
    toast(toastMessage.sentry.exportError, { variant: 'error' })
  }
}
